import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { USD } from '../../prize-box-page.constants';
import styles from '../../prize-box-page.module.css';
import { PrizeKind, type PrizeResult } from '../../prize-box-page.types';
import { handSharePctFromTopHand } from '../../prize-box-page.utils';
import { formatUsd } from '../../services/units';
import clsx from 'clsx';

import {
  bigText,
  DEFAULT_CHESTS_TO_WIN,
  DEFAULT_POINTS_TO_LOSE,
  DEFAULT_POINTS_TO_WIN,
} from './round-result.constants';

interface IRoundResultProps {
  result: PrizeResult;
  pointsAfter: number;
  attemptsLeft: number;
  onClose: () => void;
  currentSlotBalance: number;
  topHand: number;
}

export const RoundResult: FC<IRoundResultProps> = ({ result, topHand }) => {
  const { t } = useTranslation();
  const theoreticalShare = handSharePctFromTopHand(topHand);

  if (!result) return null;

  return (
    <div className="flex flex-1 flex-col items-center justify-center">
      <h2 className={styles.textWhite}>
        {result.kind === PrizeKind.Money ? t('prizeBox.youHaveWon') : t('prizeBox.youHaveGot')}
      </h2>

      {result.kind === PrizeKind.Money && (
        <div className="mt-2 flex flex-col items-center gap-5 sm:mt-8" data-testid="money-result">
          <p className={bigText}>
            {formatUsd(result.totalAmount)} {USD}
          </p>

          <div className="flex flex-col gap-2">
            <p className={clsx(styles.textWhite, 'text-center text-base')}>
              {t('prizeBox.yourTopHandEfficiency')}{' '}
              <b className={clsx(styles.textYellow, 'text-base leading-5')}>{theoreticalShare}%</b>
            </p>

            <p className={clsx(styles.textWhite, 'text-center text-xl leading-6')}>
              {t('prizeBox.yourWinning')}{' '}
              <b className={clsx(styles.textYellow, 'text-xl leading-6')}>
                {formatUsd(result.amount)} {USD}
              </b>
            </p>

            <div className="mt-8">
              <div className={styles.divider} />
              <div className={styles.warning}>
                <Link to="/nfts" className="text-[#FFDA42] underline">
                  <p>{t('prizeBox.advice')}</p>
                </Link>
              </div>
              <div className={styles.divider} />
            </div>
          </div>
        </div>
      )}

      {result.kind === PrizeKind.Points && (
        <div
          className="mt-2 flex flex-col items-center uppercase sm:mt-8"
          data-testid="points-result"
        >
          <p className={bigText}>{result.amount}</p>
          <p className={bigText}>{t('prizeBox.bonusPoints')}</p>
        </div>
      )}

      {result.kind === PrizeKind.Draw && (
        <>
          <div
            className="mt-2 flex flex-col items-center uppercase sm:mt-8"
            data-testid="draw-result"
          >
            <p className={bigText}>{DEFAULT_CHESTS_TO_WIN}</p>
            <p className={bigText}>{t('prizeBox.extraChest')}</p>
          </div>
          <div className="flex flex-col items-center sm:mt-8">
            <p className={clsx(styles.textYellow, 'text-3xl uppercase leading-normal')}>
              + {DEFAULT_POINTS_TO_WIN} {t('prizeBox.bonusPoints')}
            </p>
          </div>
        </>
      )}

      {result.kind === PrizeKind.Lose && (
        <div
          className="mt-2 flex flex-col items-center uppercase sm:mt-8"
          data-testid="lose-result"
        >
          <p className={bigText}>{DEFAULT_POINTS_TO_LOSE}</p>
          <p className={bigText}>{t('prizeBox.bonusPoints')}</p>
        </div>
      )}
    </div>
  );
};
